'use client';

import { useState, useEffect, useCallback, Suspense, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import api from '@/lib/api';
import { Img2ImgJobResponse, Img2ImgQueueStats, HealthResponse, JobResponse, Series } from '@/lib/types';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import StatusBar from '@/components/StatusBar';
import { LazyImage } from '@/components/LazyImage';

export default function Img2ImgPage() {
  return (
    <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
      <Img2ImgContent />
    </Suspense>
  );
}

function Img2ImgContent() {
  const searchParams = useSearchParams();
  const [jobs, setJobs] = useState<Img2ImgJobResponse[]>([]);
  const jobsRef = useRef<Img2ImgJobResponse[]>([]);
  const [stats, setStats] = useState<Img2ImgQueueStats | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [series, setSeries] = useState<Series[]>([]);

  // Form state
  const [sourceJobId, setSourceJobId] = useState<string | null>(null);
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(1024);
  const [steps, setSteps] = useState(9);
  const [cfg, setCfg] = useState(4.0);
  const [denoise, setDenoise] = useState(0.6);
  const [seed, setSeed] = useState<number | null>(null);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Image picker state
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [galleryJobs, setGalleryJobs] = useState<JobResponse[]>([]);

  // Selected image modal
  const [selectedJob, setSelectedJob] = useState<Img2ImgJobResponse | null>(null);

  const toast = useToast();
  const { confirm } = useConfirm();

  const fetchData = useCallback(async () => {
    try {
      const [healthRes, statsRes, jobsRes, seriesRes] = await Promise.all([
        api.getHealth(),
        api.getImg2ImgQueueStats(),
        api.listImg2ImgJobs(undefined, 50, 0),
        api.getSeries(),
      ]);
      setHealth(healthRes);
      setStats(statsRes);
      setJobs(jobsRes);
      jobsRef.current = jobsRes;
      setSeries(seriesRes);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  const fetchGalleryImages = useCallback(async () => {
    try {
      const images = await api.listJobs('completed', 100, 0);
      setGalleryJobs(images);
    } catch (error) {
      console.error('Failed to fetch gallery images:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      const hasPendingOrProcessing = jobsRef.current.some(
        j => j.status === 'pending' || j.status === 'processing'
      );
      if (hasPendingOrProcessing) {
        fetchData();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Handle source query param from ImageModal link
  useEffect(() => {
    const sourceJobIdParam = searchParams.get('source');
    if (sourceJobIdParam && !sourceJobId) {
      // Fetch the source job to get its image
      api.getJob(sourceJobIdParam).then(job => {
        if (job.status === 'completed' && job.images[0]) {
          setSourceJobId(job.job_id);
          setSourceImage(job.images[0]);
        }
      }).catch(err => {
        console.error('Failed to load source job:', err);
      });
    }
  }, [searchParams, sourceJobId]);

  const handleSelectSource = async () => {
    await fetchGalleryImages();
    setShowImagePicker(true);
  };

  const handleSourceSelected = (job: JobResponse) => {
    setSourceJobId(job.job_id);
    setSourceImage(job.images[0]);
    setShowImagePicker(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sourceJobId || !prompt.trim()) {
      toast.error('Please select a source image and enter a prompt');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.createImg2ImgJob({
        prompt: prompt.trim(),
        source_job_id: sourceJobId,
        width,
        height,
        steps,
        cfg,
        denoise,
        seed,
        series_id: selectedSeriesId,
      });
      toast.success('Img2Img job created!');
      setPrompt('');
      await fetchData();
    } catch (error) {
      console.error('Failed to create job:', error);
      toast.error('Failed to create job: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (jobId: string, force: boolean = false) => {
    try {
      await api.cancelImg2ImgJob(jobId, force);
      await fetchData();
      toast.success('Job cancelled');
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast.error('Failed to cancel job: ' + (error as Error).message);
    }
  };

  const handleDelete = async (jobId: string) => {
    const confirmed = await confirm({
      title: 'Delete Image',
      message: 'Delete this img2img job? This cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await api.deleteImg2ImgJob(jobId);
        await fetchData();
        toast.success('Job deleted');
      } catch (error) {
        console.error('Failed to delete job:', error);
        toast.error('Failed to delete job: ' + (error as Error).message);
      }
    }
  };

  const activeJobs = jobs.filter(j => j.status === 'pending' || j.status === 'processing');
  const completedJobs = jobs.filter(j => j.status === 'completed');
  const failedJobs = jobs.filter(j => j.status === 'failed');

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={health} stats={stats} onRefresh={fetchData} />

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight">Img2Img</h1>
            <p className="text-[var(--text-muted)]">Transform images with AI-guided editing</p>
          </div>

          {/* Generation Form */}
          <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Source Image Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Source Image</label>
                <div className="flex items-start gap-4">
                  {sourceImage ? (
                    <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-[var(--bg-primary)] flex-shrink-0 group">
                      <img
                        src={api.getThumbnailUrl(sourceImage)}
                        alt="Source"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => { setSourceJobId(null); setSourceImage(null); }}
                        className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors cursor-pointer"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSelectSource}
                      className="w-32 h-32 rounded-lg border-2 border-dashed border-[var(--border-subtle)] hover:border-[var(--accent)] flex flex-col items-center justify-center gap-2 text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors cursor-pointer"
                    >
                      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                      <span className="text-xs">Select Image</span>
                    </button>
                  )}
                  <div className="flex-1">
                    <p className="text-sm text-[var(--text-muted)] mb-2">
                      Select an image from your gallery to transform
                    </p>
                    {sourceImage && (
                      <button
                        type="button"
                        onClick={handleSelectSource}
                        className="text-sm text-[var(--accent)] hover:underline cursor-pointer"
                      >
                        Change image
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium mb-2">Prompt</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the transformation (e.g., 'same scene at sunset', 'add warm golden lighting', 'winter scene with snow')"
                  className="w-full px-4 py-3 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none resize-none"
                  rows={3}
                />
                <p className="text-xs text-[var(--text-muted)] mt-2">
                  Best for: style changes, lighting adjustments, seasonal variations. Note: Background replacement requires high denoise (0.8+) and may change the subject.
                </p>
              </div>

              {/* Denoise Strength */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Denoise Strength: {denoise.toFixed(2)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={denoise}
                  onChange={(e) => setDenoise(parseFloat(e.target.value))}
                  className="w-full accent-[var(--accent)]"
                />
                <div className="flex justify-between text-xs text-[var(--text-muted)] mt-1">
                  <span>0.0 - Preserve original</span>
                  <span>1.0 - Full transformation</span>
                </div>
                <p className="text-xs text-[var(--text-muted)] mt-2">
                  Lower values preserve more of the original image. Recommended: 0.4-0.7
                </p>
              </div>

              {/* Settings Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text-muted)] mb-1">Width</label>
                  <select
                    value={width}
                    onChange={(e) => setWidth(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none cursor-pointer"
                  >
                    <option value={512}>512</option>
                    <option value={768}>768</option>
                    <option value={1024}>1024</option>
                    <option value={1280}>1280</option>
                    <option value={1536}>1536</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-muted)] mb-1">Height</label>
                  <select
                    value={height}
                    onChange={(e) => setHeight(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none cursor-pointer"
                  >
                    <option value={512}>512</option>
                    <option value={768}>768</option>
                    <option value={1024}>1024</option>
                    <option value={1280}>1280</option>
                    <option value={1536}>1536</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-muted)] mb-1">Steps</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={steps}
                    onChange={(e) => setSteps(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-muted)] mb-1">CFG (1-7)</label>
                  <input
                    type="number"
                    min={1}
                    max={7}
                    step={0.5}
                    value={cfg}
                    onChange={(e) => setCfg(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  />
                </div>
              </div>

              {/* Seed and Series Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text-muted)] mb-1">Seed (optional)</label>
                  <input
                    type="number"
                    value={seed ?? ''}
                    onChange={(e) => setSeed(e.target.value ? Number(e.target.value) : null)}
                    placeholder="Random"
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-muted)] mb-1">Series</label>
                  <select
                    value={selectedSeriesId ?? ''}
                    onChange={(e) => setSelectedSeriesId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none cursor-pointer"
                  >
                    <option value="">No series</option>
                    {series.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || !sourceJobId || !prompt.trim()}
                className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmitting ? 'Creating...' : 'Transform Image'}
              </button>
            </form>
          </div>

          {/* Queue Stats */}
          {stats && (stats.pending > 0 || stats.processing > 0) && (
            <div className="bg-[var(--bg-elevated)] rounded-lg p-4 flex items-center gap-6">
              {stats.processing > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                  <span className="text-sm">{stats.processing} processing</span>
                </div>
              )}
              {stats.pending > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                  <span className="text-sm">{stats.pending} in queue</span>
                </div>
              )}
            </div>
          )}

          {/* Active Jobs */}
          {activeJobs.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                In Progress
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {activeJobs.map((job) => (
                  <Img2ImgJobCard
                    key={job.job_id}
                    job={job}
                    onCancel={handleCancel}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Failed Jobs */}
          {failedJobs.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-sm font-medium text-red-400 uppercase tracking-wider">
                Failed
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {failedJobs.map((job) => (
                  <Img2ImgJobCard
                    key={job.job_id}
                    job={job}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Completed Gallery */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                Results
              </h2>
              {completedJobs.length > 0 && (
                <span className="text-xs text-[var(--text-muted)]">
                  {completedJobs.length} images
                </span>
              )}
            </div>

            {completedJobs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-[var(--bg-elevated)] flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
                  </svg>
                </div>
                <p className="text-[var(--text-muted)]">No transformed images yet</p>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  Select a source image and describe your transformation
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {completedJobs.map((job) => (
                  <Img2ImgJobCard
                    key={job.job_id}
                    job={job}
                    onSelect={setSelectedJob}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      {/* Image Picker Modal */}
      {showImagePicker && (
        <ImagePickerModal
          images={galleryJobs}
          onSelect={handleSourceSelected}
          onClose={() => setShowImagePicker(false)}
        />
      )}

      {/* Result Image Modal */}
      {selectedJob && selectedJob.status === 'completed' && (
        <Img2ImgResultModal
          job={selectedJob}
          jobs={completedJobs}
          onClose={() => setSelectedJob(null)}
          onNavigate={setSelectedJob}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

// Img2Img Job Card Component
function Img2ImgJobCard({
  job,
  onSelect,
  onCancel,
  onDelete,
}: {
  job: Img2ImgJobResponse;
  onSelect?: (job: Img2ImgJobResponse) => void;
  onCancel?: (jobId: string, force?: boolean) => void;
  onDelete?: (jobId: string) => void;
}) {
  const isActive = job.status === 'pending' || job.status === 'processing';
  const isFailed = job.status === 'failed';

  return (
    <div
      className={`group relative rounded-xl overflow-hidden bg-[var(--bg-elevated)] border border-[var(--border-subtle)] transition-all ${
        job.status === 'completed' && onSelect ? 'cursor-pointer hover:border-[var(--accent)]' : ''
      }`}
      onClick={() => job.status === 'completed' && onSelect?.(job)}
    >
      <div className="aspect-square relative">
        {job.status === 'completed' && job.images[0] ? (
          <LazyImage
            src={api.getThumbnailUrl(job.images[0])}
            fallbackSrc={api.getImageUrl(job.images[0])}
            alt={job.prompt}
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[var(--bg-primary)]">
            {isActive && (
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-[var(--text-muted)]">
                  {job.status === 'pending' ? `Queue #${job.position ?? '?'}` : 'Processing'}
                </span>
              </div>
            )}
            {isFailed && (
              <div className="flex flex-col items-center gap-2 text-red-400 p-4">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <span className="text-xs text-center">{job.error || 'Failed'}</span>
              </div>
            )}
          </div>
        )}

        {/* Source image thumbnail */}
        {job.source_image && (
          <div className="absolute top-2 left-2 w-10 h-10 rounded-md overflow-hidden border border-white/30 shadow-lg">
            <LazyImage
              src={api.getThumbnailUrl(job.source_image)}
              fallbackSrc={api.getImageUrl(job.source_image)}
              alt="Source"
              className="w-full h-full"
            />
          </div>
        )}

        {/* Actions overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {isActive && onCancel && (
            <button
              onClick={(e) => { e.stopPropagation(); onCancel(job.job_id); }}
              className="p-2 bg-red-500/80 hover:bg-red-500 rounded-lg text-white transition-colors cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {(job.status === 'completed' || isFailed) && onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(job.job_id); }}
              className="p-2 bg-red-500/80 hover:bg-red-500 rounded-lg text-white transition-colors cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-sm truncate">{job.prompt}</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          {job.width}x{job.height} | Denoise: {job.denoise}
        </p>
      </div>
    </div>
  );
}

// Image Picker Modal
function ImagePickerModal({
  images,
  onSelect,
  onClose,
}: {
  images: JobResponse[];
  onSelect: (job: JobResponse) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm animate-fadeIn flex items-center justify-center p-4">
      <div className="bg-[var(--bg-elevated)] rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)]">
          <h2 className="text-xl font-semibold">Select Source Image</h2>
          <button
            onClick={onClose}
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-primary)] rounded-lg transition-all cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {images.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-[var(--text-muted)]">No images in gallery</p>
              <p className="text-sm text-[var(--text-muted)] mt-1">
                Generate some images first
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {images.map((job) => (
                <button
                  key={job.job_id}
                  onClick={() => onSelect(job)}
                  className="aspect-square rounded-lg overflow-hidden bg-[var(--bg-primary)] hover:ring-2 hover:ring-[var(--accent)] transition-all cursor-pointer"
                >
                  {job.images[0] && (
                    <LazyImage
                      src={api.getThumbnailUrl(job.images[0])}
                      fallbackSrc={api.getImageUrl(job.images[0])}
                      alt={job.prompt}
                      className="w-full h-full"
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Result Modal
function Img2ImgResultModal({
  job,
  jobs,
  onClose,
  onNavigate,
  onDelete,
}: {
  job: Img2ImgJobResponse;
  jobs: Img2ImgJobResponse[];
  onClose: () => void;
  onNavigate: (job: Img2ImgJobResponse) => void;
  onDelete: (jobId: string) => void;
}) {
  const currentIndex = jobs.findIndex(j => j.job_id === job.job_id);
  const prevJob = currentIndex > 0 ? jobs[currentIndex - 1] : null;
  const nextJob = currentIndex < jobs.length - 1 ? jobs[currentIndex + 1] : null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && prevJob) onNavigate(prevJob);
      else if (e.key === 'ArrowRight' && nextJob) onNavigate(nextJob);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, onNavigate, prevJob, nextJob]);

  if (job.status !== 'completed' || job.images.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 animate-fadeIn flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 h-14 bg-black/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            title="Close (Esc)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="text-white/40 text-sm">
            {currentIndex + 1} / {jobs.length}
          </span>
        </div>
        <p className="text-white/80 text-sm truncate max-w-xl">{job.prompt}</p>
        <div className="flex items-center gap-2">
          <a
            href={api.getImageUrl(job.images[0])}
            download
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            title="Download"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </a>
          <button
            onClick={() => onDelete(job.job_id)}
            className="p-2 text-white/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all cursor-pointer"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </button>
        </div>
      </div>

      {/* Main content with side-by-side comparison */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Previous button */}
        {prevJob && (
          <button
            onClick={() => onNavigate(prevJob)}
            className="absolute left-4 z-10 p-3 text-white/40 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all cursor-pointer"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
        )}

        {/* Comparison view */}
        <div className="flex items-center gap-4 p-8 max-w-full">
          {/* Source image */}
          {job.source_image && (
            <div className="flex flex-col items-center gap-2">
              <span className="text-white/40 text-xs uppercase tracking-wider">Source</span>
              <img
                src={api.getImageUrl(job.source_image)}
                alt="Source"
                className="max-h-[70vh] max-w-[40vw] object-contain rounded-lg"
              />
            </div>
          )}

          {/* Arrow */}
          <div className="text-white/20">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </div>

          {/* Result image */}
          <div className="flex flex-col items-center gap-2">
            <span className="text-white/40 text-xs uppercase tracking-wider">Result</span>
            <img
              src={api.getImageUrl(job.images[0])}
              alt={job.prompt}
              className="max-h-[70vh] max-w-[40vw] object-contain rounded-lg"
            />
          </div>
        </div>

        {/* Next button */}
        {nextJob && (
          <button
            onClick={() => onNavigate(nextJob)}
            className="absolute right-4 z-10 p-3 text-white/40 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all cursor-pointer"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        )}
      </div>

      {/* Bottom info */}
      <div className="px-4 py-3 bg-black/50 backdrop-blur-sm">
        <div className="flex items-center justify-center gap-6 text-sm text-white/60">
          <span>{job.width}x{job.height}</span>
          <span>Steps: {job.steps}</span>
          <span>CFG: {job.cfg}</span>
          <span>Denoise: {job.denoise}</span>
          {job.seed && <span>Seed: {job.seed}</span>}
        </div>
      </div>
    </div>
  );
}
